/**
 * Unified Device Connection Store
 *
 * 3-layer state:
 *   protocol → 통신 방식 (ble | serial)
 *   device   → 연결된 기기 정보
 *   adapter  → 연결 하드웨어 (direct | cradle | dock)
 *
 * Rules:
 *   - Only one protocol can be active at a time
 *   - Connecting a new protocol auto-disconnects the previous one
 *   - device.type === 'none' means no confirmed device (either not connected or serial not loaded yet)
 */

import { create } from 'zustand';
import { BAUD_RATE } from '@packages/protocols/constants';
import {
  type GlobalConnectionState,
  type ConnectionActionResult,
  type DeviceCloud,
  type AdapterState,
  type ConfirmedDeviceData,
} from '../types/connection';

// ── Action param types ───────────────────────────────────────

interface ConnectBleParams {
  sessionId: string;
  device: ConfirmedDeviceData;
}

interface ConnectSerialParams {
  sessionId: string;
  portName: string;
  baudRate?: number;
  adapter: Extract<AdapterState, { type: 'cradle' | 'dock' }>;
}

// ── Store interface ──────────────────────────────────────────

interface ConnectionStore extends GlobalConnectionState {
  // 연결
  connectBle(params: ConnectBleParams): Promise<ConnectionActionResult>;
  connectSerial(params: ConnectSerialParams): Promise<ConnectionActionResult>;
  disconnect(): Promise<ConnectionActionResult>;

  // 기기 확정 (serial: 기기 정보 읽은 후 호출)
  confirmDevice(deviceData: ConfirmedDeviceData): void;

  // 클라우드 정보 업데이트 (Sites 페이지에서 호출)
  updateDeviceCloud(cloud: DeviceCloud | null): void;

  // 기타
  reset(): void;
  setDownloading(isDownloading: boolean): void;
}

// ── Initial state ────────────────────────────────────────────

const initialState: GlobalConnectionState = {
  protocol: { type: 'none' },
  device: { type: 'none' },
  adapter: { type: 'none' },
  sessionId: null,
  lastConnectedDeviceId: null,
  updatedAt: null,
  isDownloading: false,
};

// ── Store ────────────────────────────────────────────────────

export const useConnectionStore = create<ConnectionStore>((set, get) => ({
  ...initialState,

  /**
   * BLE 연결 - 기기 정보까지 한 번에 저장
   * BLE는 연결 즉시 device info를 읽으므로 device state가 바로 확정됨
   */
  connectBle: async params => {
    const { protocol } = get();

    if (protocol.type === 'serial') {
      await get().disconnect();
    }

    set({
      protocol: { type: 'ble' },
      adapter: { type: 'direct' },
      device: {
        ...params.device,
        cloud: null,
      },
      sessionId: params.sessionId,
      lastConnectedDeviceId: params.device.activeDeviceId,
      updatedAt: new Date(),
    });

    return { success: true, data: { deviceId: params.device.activeDeviceId } };
  },

  /**
   * Serial 연결 - 포트만 열고 device는 'none' 상태
   * 기기 정보는 confirmDevice()로 별도 저장
   */
  connectSerial: async params => {
    const { protocol } = get();

    if (protocol.type === 'ble') {
      await get().disconnect();
    }

    set({
      protocol: {
        type: 'serial',
        portName: params.portName,
        baudRate: params.baudRate ?? BAUD_RATE,
      },
      adapter: params.adapter,
      device: { type: 'none' },
      sessionId: params.sessionId,
      updatedAt: new Date(),
    });

    return { success: true };
  },

  /**
   * 연결 해제 - 모든 상태 초기화
   */
  disconnect: async () => {
    const { device } = get();
    const lastId = device.type !== 'none' ? device.activeDeviceId : get().lastConnectedDeviceId;

    set({
      protocol: { type: 'none' },
      adapter: { type: 'none' },
      device: { type: 'none' },
      sessionId: null,
      lastConnectedDeviceId: lastId,
      updatedAt: new Date(),
    });

    return { success: true };
  },

  /**
   * Serial 기기 확정 - 기기 정보를 읽은 후 호출
   * BLE는 connectBle()에서 바로 처리되므로 serial 전용
   */
  confirmDevice: deviceData => {
    set({
      device: {
        ...deviceData,
        cloud: null,
      },
      lastConnectedDeviceId: deviceData.activeDeviceId,
      updatedAt: new Date(),
    });
  },

  /**
   * 클라우드 정보 업데이트 (Sites 페이지에서 기기 등록 확인 후 호출)
   */
  updateDeviceCloud: cloud => {
    const { device } = get();
    if (device.type === 'none') return;

    set({
      device: { ...device, cloud },
      updatedAt: new Date(),
    });
  },

  reset: () => {
    set({ ...initialState, updatedAt: new Date() });
  },

  setDownloading: isDownloading => {
    set({ isDownloading });
  },
}));

// ── Development helpers ──────────────────────────────────────

if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).__CONNECTION_STORE__ = useConnectionStore;

  (window as any).getConnectionState = () => {
    const state = useConnectionStore.getState();
    return state;
  };

  (window as any).watchConnectionStore = () => {
    console.log('👀 Watching connection store changes...');
    return useConnectionStore.subscribe(state => {
      console.log('🔄 [Store Update]', {
        protocol: state.protocol,
        device: state.device,
        adapter: state.adapter,
      });
    });
  };

  // console.log('🛠️ Connection Store Debug Tools Available:');
  // console.log('  - getConnectionState() : 현재 상태 확인');
  // console.log('  - watchConnectionStore() : 실시간 변화 감지');
  // console.log('  - __CONNECTION_STORE__.getState() : 전체 상태');
}
